/**
 * Single source of truth for the languages the shell ships.
 *
 * Adding a language means: extend `SUPPORTED_LANGUAGES` and add the matching
 * `public/i18n/<code>.json` dictionary. Nothing else.
 */

export const SUPPORTED_LANGUAGES = ['en', 'de'] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Used when the browser reports no language we ship. English rather than
 * German — it is the safer fallback for a visitor we cannot identify.
 */
export const DEFAULT_LANGUAGE: LanguageCode = 'en';

/** How each language names ITSELF — never translated: whoever looks for it looks for this word. */
export const LANGUAGE_NAMES: Readonly<Record<LanguageCode, string>> = {
  en: 'English',
  de: 'Deutsch',
};

/** The two letters the header switcher shows. */
export const LANGUAGE_SHORT: Readonly<Record<LanguageCode, string>> = {
  en: 'EN',
  de: 'DE',
};

/**
 * Where the chosen language is remembered between visits. The same key the
 * UMC uses, so the two applications read as one product.
 */
export const LANGUAGE_STORAGE_KEY = 'sa.lang';

/**
 * Dictionary key whose subtree is handed to PrimeNG's own translation service
 * (button labels, date names, filter operators).
 */
export const PRIMENG_TRANSLATION_KEY = 'primeng';

export function isSupportedLanguage(value: string | null | undefined): value is LanguageCode {
  return value != null && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Narrows a BCP 47 tag (`de-AT`, `en-GB`) to a supported language code, or
 * returns `null` when nothing matches.
 */
export function toSupportedLanguage(tag: string | null | undefined): LanguageCode | null {
  if (!tag) {
    return null;
  }

  const base = tag.split('-')[0]?.toLowerCase();

  return isSupportedLanguage(base) ? base : null;
}
